// ============================================
// COMPANY USER PERMISSIONS
// ============================================
// Company Admin interface for managing user permissions within the company
// - View all company users
// - Set granular permissions per user
// - Toggle access to specific features/modules

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Shield, Users, Save, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface CompanyUser {
  id: string;
  email: string;
  name?: string;
  isCompanyAdmin: boolean;
  permissions?: {
    canViewProjects?: boolean;
    canEditProjects?: boolean;
    canViewWorkflows?: boolean;
    canEditWorkflows?: boolean;
    canViewReports?: boolean;
    canManageUsers?: boolean;
  };
}

interface CompanyUserPermissionsProps {
  accessToken: string;
  companyId: string;
  currentUser: any;
}

// Available permissions for company users
const USER_PERMISSIONS = [
  { key: 'canViewProjects', label: 'View Projects', description: 'Can view project information' },
  { key: 'canEditProjects', label: 'Edit Projects', description: 'Can modify project details' },
  { key: 'canViewWorkflows', label: 'View Workflows', description: 'Can view workflow configurations' },
  { key: 'canEditWorkflows', label: 'Edit Workflows', description: 'Can modify workflows' },
  { key: 'canViewReports', label: 'View Reports', description: 'Can access reports and analytics' },
  { key: 'canManageUsers', label: 'Manage Users', description: 'Can invite and manage other users (Admin only)' },
];

export function CompanyUserPermissions({
  accessToken,
  companyId,
  currentUser,
}: CompanyUserPermissionsProps) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState<Map<string, any>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  async function loadUsers() {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        
        // Initialize permission changes map
        const initialChanges = new Map();
        (data.users || []).forEach((user: CompanyUser) => {
          initialChanges.set(user.id, user.permissions || {});
        });
        setPermissionChanges(initialChanges);
      } else {
        const errorText = await response.text();
        console.error('Failed to load users:', errorText);
        toast.error('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  }

  function togglePermission(userId: string, permissionKey: string) {
    const newChanges = new Map(permissionChanges);
    const userPermissions = { ...(newChanges.get(userId) || {}) };
    
    userPermissions[permissionKey] = !userPermissions[permissionKey];
    
    newChanges.set(userId, userPermissions);
    setPermissionChanges(newChanges);
    
    // Check if there are any changes from original
    const hasAnyChanges = Array.from(newChanges.entries()).some(([id, perms]) => {
      const original = users.find(u => u.id === id);
      if (!original) return false;
      
      return JSON.stringify(perms) !== JSON.stringify(original.permissions || {});
    });
    
    setHasChanges(hasAnyChanges);
  }

  async function saveChanges() {
    try {
      setSaving(true);
      
      // Prepare updates for users whose permissions changed
      const updates = [];
      for (const [userId, newPerms] of permissionChanges.entries()) {
        const original = users.find(u => u.id === userId);
        if (!original) continue;
        
        const hasChanged = JSON.stringify(newPerms) !== JSON.stringify(original.permissions || {});
        
        if (hasChanged) {
          updates.push({
            userId,
            permissions: newPerms,
          });
        }
      }
      
      if (updates.length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      // Send batch update to backend
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/users/permissions`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }
      );
      
      if (response.ok) {
        toast.success(`Updated permissions for ${updates.length} user(s)`);
        setHasChanges(false);
        await loadUsers(); // Reload to get fresh data
      } else {
        const errorText = await response.text();
        console.error('Failed to update permissions:', errorText);
        toast.error('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error saving permission changes:', error);
      toast.error('Error saving permission changes');
    } finally {
      setSaving(false);
    }
  }

  function resetChanges() {
    // Reset to original permissions
    const originalChanges = new Map();
    users.forEach(user => {
      originalChanges.set(user.id, user.permissions || {});
    });
    setPermissionChanges(originalChanges);
    setHasChanges(false);
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="size-5 text-blue-500" />
              User Permissions
            </CardTitle>
            <CardDescription>
              Manage what each user can access and modify
            </CardDescription>
          </div>
          {hasChanges && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetChanges} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={saveChanges} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="size-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {hasChanges && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              You have unsaved changes. Click "Save Changes" to apply permission updates.
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <Info className="size-4" />
          <AlertDescription>
            <strong>Note:</strong> Company Admins automatically have all permissions. Regular users can be granted specific permissions below.
          </AlertDescription>
        </Alert>

        {users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="size-12 mx-auto mb-3 text-gray-400" />
            <p className="text-muted-foreground">No users found in this company</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">User</TableHead>
                  {USER_PERMISSIONS.map(perm => (
                    <TableHead key={perm.key} className="text-center min-w-[120px]">
                      <div className="space-y-1">
                        <div className="text-xs">{perm.label}</div>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => {
                  const userPerms = permissionChanges.get(user.id) || {};
                  const isCurrentUser = user.id === currentUser.id;
                  const isAdmin = user.isCompanyAdmin;
                  
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{user.name || user.email}</span>
                            {isCurrentUser && (
                              <Badge variant="outline" className="text-xs">You</Badge>
                            )}
                            {isAdmin && (
                              <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                                Admin
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </TableCell>
                      {USER_PERMISSIONS.map(perm => {
                        const hasPermission = isAdmin || userPerms[perm.key];
                        const isDisabled = isAdmin; // Admins always have all permissions
                        
                        return (
                          <TableCell key={perm.key} className="text-center">
                            <div className="flex items-center justify-center">
                              {isAdmin ? (
                                <CheckCircle2 className="size-4 text-purple-500" />
                              ) : (
                                <Checkbox
                                  checked={hasPermission}
                                  onCheckedChange={() => togglePermission(user.id, perm.key)}
                                  disabled={isDisabled}
                                  aria-label={`Toggle ${perm.label} for ${user.name || user.email}`}
                                />
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Permission Descriptions */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Shield className="size-4" />
            Permission Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {USER_PERMISSIONS.map(perm => (
              <div key={perm.key} className="flex items-start gap-2">
                <CheckCircle2 className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-sm">{perm.label}</div>
                  <div className="text-xs text-muted-foreground">{perm.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
